import React, { useState } from 'react';
import { FiArrowLeft, FiChevronRight } from 'react-icons/fi';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '../../utils/toast';
import { useManualSignUpMutation, useUpdateProfileMutation } from '../../api/auth';
import { createGenresAfterSignup } from '../../api/genres';
import { useAuth } from '../../hooks/useAuth';
import { signupStore } from '../../utils/signupStore';
import { mapUserFromAuthResponse } from '../../contexts/AuthContext';
import { LocationSearchInput } from '../common/LocationSearchInput';
import type { LocationBody } from '../../services/locationService';
import { apiClient } from '../../api/api';
import { AuthHeader } from './AuthHeader';

const normalizeGenreSlugs = (genres: string[]): string[] =>
  Array.from(
    new Set(genres.map((genre) => genre.trim().toLowerCase()).filter((genre) => genre.length > 0)),
  );

const toSlugCandidate = (value: string): string => value.trim().toLowerCase().replace(/\s+/g, '-');

const extractProfileGenreSlugs = (profilePayload: any): string[] => {
  const profile = profilePayload?.profile ?? profilePayload?.data ?? profilePayload ?? {};
  const rawGenres = Array.isArray(profile?.genres) ? profile.genres : [];

  return normalizeGenreSlugs(
    rawGenres
      .map((genre: unknown) => {
        if (typeof genre === 'string') return toSlugCandidate(genre);
        if (genre && typeof genre === 'object') {
          if ('slug' in genre && typeof (genre as any).slug === 'string')
            return (genre as any).slug.trim().toLowerCase();
          if ('name' in genre && typeof (genre as any).name === 'string')
            return toSlugCandidate((genre as any).name);
        }
        return '';
      })
      .filter(Boolean),
  );
};

const buildGenreSlugMap = async (): Promise<Map<string, string>> => {
  const { data } = await apiClient.get('/custom_account/genres/?page=1');
  const rows = Array.isArray(data?.data) ? data.data : [];
  const map = new Map<string, string>();

  rows.forEach((genre: any) => {
    const slug = typeof genre?.slug === 'string' ? genre.slug.trim().toLowerCase() : '';
    const name = typeof genre?.name === 'string' ? genre.name.trim().toLowerCase() : '';
    if (!slug) return;
    map.set(slug, slug);
    if (name) map.set(name, slug);
  });

  return map;
};

export const SearchLocationPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { login, updateUser } = useAuth();
  const signUpMutation = useManualSignUpMutation();
  const updateProfileMutation = useUpdateProfileMutation();

  const isSignupFlow = signupStore.isActive();

  // Restore any previously selected location from the store
  const stored = signupStore.get().locationBody ?? null;
  const [selectedLocation, setSelectedLocation] = useState<LocationBody | null>(stored);
  const [isLoading, setIsLoading] = useState(false);

  const handleLocationSelected = (location: LocationBody | null) => {
    setSelectedLocation(location);
    // Persist to store immediately so Back-navigation retains the selection
    if (location) {
      signupStore.set({ locationBody: location });
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!isSignupFlow) {
      if (!selectedLocation) {
        toast.error('Please select a location before continuing.');
        return;
      }
      // Post-login flow – just navigate home (location is informational for now)
      navigate({ to: '/' });
      return;
    }

    const draft = signupStore.get();

    // Guard: ensure required signup fields are present
    const isGoogle = !!draft.googleSignedUp;
    const isApple = !!draft.appleSignedUp;
    const hasPasswordIfRequired = isGoogle || isApple || !!draft.password;

    if (!draft.email || !hasPasswordIfRequired || !draft.username || !draft.fullName) {
      toast.error('Some signup information is missing. Please start over.');
      signupStore.clear();
      navigate({ to: '/sign-up' });
      return;
    }

    const locationBody = selectedLocation
      ? {
          address: selectedLocation.address,
          city: selectedLocation.city,
          state: selectedLocation.state,
          country: selectedLocation.country,
          latitude: selectedLocation.latitude,
          longitude: selectedLocation.longitude,
        }
      : {
          address: '',
          city: '',
          state: '',
          country: '',
          latitude: 0,
          longitude: 0,
        };
    const customGenreNames = draft.customGenres ?? [];
    // Only send genres that actually exist in the API — custom ones are created post-signup
    const apiGenres = (draft.genres ?? []).filter((g) => !customGenreNames.includes(g));

    // Role-selection step is skipped in onboarding. If role is absent, default to creator path.
    const signupUserType = draft.role === 'fan' ? 'fan' : 'event_producer';
    const signupBody: Record<string, any> = {
      full_name: draft.fullName,
      username: draft.username,
      user_types: [signupUserType],
      date_of_birth: draft.dateOfBirth ?? '',
      email: draft.email,
      genres: apiGenres,
      google_signed_up: isGoogle,
      is_apple_user: isApple,
      apple_user_id: isApple ? (draft.appleUserId ?? '') : '',
      location_body: locationBody,
    };

    // Social signups should not send a password field.
    if (!isGoogle && !isApple && draft.password) {
      signupBody.password = draft.password;
    }

    const manualImage = signupStore.getProfileImage();
    const googleImageUrl = (draft.profilePicUrl || draft.profileImageUrl || '').trim();

    // Mirror mobile SignupDto.toMap(): Google photo goes inside the JSON body as
    // google_prof_pics — NOT as a prof_pics form field.
    if (isGoogle && googleImageUrl) {
      signupBody.google_prof_pics = googleImageUrl;
    }

    setIsLoading(true);
    try {
      // Mirror mobile: send the raw File directly as multipart prof_pics.
      // Mobile never pre-uploads to Cloudinary during signup — it streams the
      // file straight to the backend signup endpoint.
      const data = await signUpMutation.mutateAsync({
        body: signupBody,
        // For manual signup: raw File object (sent as binary multipart field)
        // For Google signup: undefined — photo is already in the JSON body above
        profileImage: !isGoogle && manualImage ? manualImage : undefined,
      });

      // Extract token + user then log in (matches provided response structure: { data: {...}, tokens: {...} })
      const token: string = data?.tokens?.access ?? data?.access_token ?? data?.token ?? '';
      const refreshToken: string | undefined =
        data?.tokens?.refresh ?? data?.refresh ?? data?.refresh_token;
      const userDataRaw = data?.data ?? data?.user ?? { id: '', email: draft.email ?? '' };
      const mappedUser = mapUserFromAuthResponse(userDataRaw);
      if (!token) {
        throw new Error('Missing access token from signup response.');
      }

      login(token, mappedUser, refreshToken);

      signupStore.clear();
      navigate({ to: '/' });

      // Run genre sync in background after redirect.
      void (async () => {
        try {
          const genreSlugMap = await buildGenreSlugMap();
          const selectedGenreSlugs = (apiGenres ?? []).map((genre) => {
            const key = (typeof genre === 'string' ? genre : '').trim().toLowerCase();
            return genreSlugMap.get(key) ?? toSlugCandidate(typeof genre === 'string' ? genre : '');
          });

          let createdCustomGenreSlugs: string[] = [];
          if (customGenreNames.length > 0) {
            const newlyCreatedSlugs = await createGenresAfterSignup(token, customGenreNames);
            const fallbackSlugs = customGenreNames.map((name) => {
              const key = name.trim().toLowerCase();
              return genreSlugMap.get(key) ?? toSlugCandidate(name);
            });
            createdCustomGenreSlugs = Array.from(new Set([...newlyCreatedSlugs, ...fallbackSlugs]));
          }

          const profileResponse = mappedUser.id
            ? await apiClient.get(`/profiles/${mappedUser.id}/`)
            : { data: mappedUser };
          const profileGenreSlugs = extractProfileGenreSlugs(profileResponse.data);
          const mergedGenres = normalizeGenreSlugs([
            ...profileGenreSlugs,
            ...selectedGenreSlugs,
            ...createdCustomGenreSlugs,
          ]);

          await updateProfileMutation.mutateAsync({ genres: mergedGenres, silent: true });
          updateUser({ genres: mergedGenres });

          // Invalidate profile query caches so components fetch updated data
          queryClient.invalidateQueries({ queryKey: ['profile'] });
          queryClient.invalidateQueries({ queryKey: ['public-profile'] });
          queryClient.invalidateQueries({ queryKey: ['user-profile'] });
        } catch (syncError: unknown) {
          console.error('Post-signup genre sync failed:', syncError);
          // Silent on failure as requested by the user
        }
      })();
    } catch (error: unknown) {
      console.error('Signup failed:', error);
      // Error toast is handled by the mutation onError
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex bg-[#030303] flex-col overflow-hidden font-['Inter'] selection:bg-blue-500/30">
      {/* Background Glow */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          width: '1308px',
          height: '449px',
          bottom: '-150px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(to right, #2F80ED, #942FED)',
          opacity: 0.5,
          filter: 'blur(260px)',
          zIndex: 0,
        }}
      />

      <AuthHeader />

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center w-full max-w-160 mx-auto px-6 py-20">
        {/* Navigation Actions */}
        <div className="w-full flex justify-between items-center mb-6">
          <button
            onClick={() => window.history.back()}
            className="w-10 h-10 rounded-full bg-[#1a1a1a] hover:bg-[#2a2a2a] flex items-center justify-center transition-colors border border-white/5 cursor-pointer shadow-lg"
            aria-label="Go back"
          >
            <FiArrowLeft className="text-zinc-300" size={18} />
          </button>

          <button
            onClick={() => handleSubmit()}
            disabled={isLoading}
            className="text-[#3B82F6] hover:text-blue-400 font-medium text-[15px] flex items-center gap-1 transition-colors group cursor-pointer disabled:opacity-50"
          >
            Skip{' '}
            <FiChevronRight
              size={18}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </button>
        </div>

        {/* Title */}
        <div className="w-full text-center mb-8">
          <h1
            className="text-white text-[32px] md:text-[40px] font-medium leading-[1.1] mb-2"
            style={{ fontFamily: "'Funnel Display', sans-serif" }}
          >
            Select Location
          </h1>
          <p className="text-[#a1a1aa] text-[14px] md:text-[15px] font-medium">
            {isSignupFlow
              ? 'Almost there \u2014 pick your city to complete sign up'
              : 'Help us connect you with nearby artists and events'}
          </p>
        </div>

        {/* Location Search Component */}
        <form className="w-full flex flex-col gap-6" onSubmit={handleSubmit}>
          <LocationSearchInput
            onLocationSelected={handleLocationSelected}
            initialLocation={stored}
          />

          <button
            type="submit"
            disabled={isLoading}
            className="h-14 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold text-[16px] rounded-2xl transition-all shadow-xl shadow-blue-500/20 active:scale-[0.98] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isLoading
              ? isSignupFlow
                ? 'Creating your account\u2026'
                : 'Saving\u2026'
              : isSignupFlow
                ? 'Create Account'
                : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
};
